import { Download, Edit, NoteAlt } from "@mui/icons-material";
import {
    AppBar,
    Box,
    Dialog,
    DialogContent,
    IconButton,
    TableCell,
    TableRow,
    Toolbar,
    Typography,
} from "@mui/material";
import { GridCloseIcon } from "@mui/x-data-grid";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import React, { Dispatch, useState } from "react";
import { IContract } from "../../../interfaces/IContract";
import { IContractState } from "../../../interfaces/IContractState";
import { IContractType } from "../../../interfaces/IContractType";
import { IDocument } from "../../../interfaces/IDocument";
import { IProject } from "../../../interfaces/IProject";
import { IService } from "../../../interfaces/IService";
import { IServiceCategory } from "../../../interfaces/IServiceCategory";
import { ITemplate } from "../../../interfaces/ITemplate";
import { IUnit } from "../../../interfaces/IUnit";
import { useSnackbar } from "../../context/SnackbarContext";
import { base64Download } from "../../core/generic";
import Transition from "../../core/Transition";
import { EditorContract } from "./EditorContract";
import { SignedContract } from "./SignedContract";
import { useGeneratePdf } from "../../../utlis/Document";

interface IProps {
    project: IProject;
    contractObject: IContract;
    setContractArray: Dispatch<React.SetStateAction<IContract[]>>;
    contractStateArray: IContractState[];
    contractTypeArray: IContractType[];
    //
    templateArray: ITemplate[];
    serviceCategoryArray: IServiceCategory[];
    serviceArray: IService[];
    unitArray: IUnit[];
    //
    refetchContractArray: (options?: RefetchOptions) => Promise<QueryObserverResult<IContract[], Error>>;
}

export const ContractRow: React.FC<IProps> = ({
    project,
    contractObject,
    setContractArray,
    contractStateArray,
    contractTypeArray,
    templateArray,
    serviceCategoryArray,
    serviceArray,
    unitArray,
    refetchContractArray,
}) => {
    const { showFeedback } = useSnackbar();

    const { generatePdf, isPendingGenerateContractDocument } = useGeneratePdf();
    const [generatedData, setGeneratedData] = useState<IDocument | null>(null);
    //
    const [editContract, setEditContract] = useState(contractObject);
    //
    const [isOpenEditor, setIsOpenEditor] = useState(false);
    const [isOpenSigned, setIsOpenSigned] = useState(false);

    const handleOpenEditor = () => {
        setEditContract(contractObject);
        setIsOpenEditor(true);
    };

    return (
        <>
            {isOpenEditor && (
                <Dialog
                    fullScreen
                    open={isOpenEditor}
                    onClose={() => setIsOpenEditor(false)}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: "relative" }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => setIsOpenEditor(false)}
                                aria-label="close"
                            >
                                <GridCloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                Vertragseditor
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ m: 5 }}>
                        <EditorContract
                            key={`editor-${contractObject.id}`}
                            project={project}
                            orgContract={contractObject}
                            editContract={editContract}
                            setEditContract={setEditContract}
                            contractStateArray={contractStateArray}
                            contractTypeArray={contractTypeArray}
                            serviceCategoryArray={serviceCategoryArray}
                            serviceArray={serviceArray}
                            unitArray={unitArray}
                            handleClose={() => setIsOpenEditor(false)}
                            handleSave={refetchContractArray}
                        />
                    </Box>
                </Dialog>
            )}

            {isOpenSigned && (
                <Dialog
                    fullScreen
                    open={isOpenSigned}
                    onClose={() => setIsOpenSigned(false)}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: "relative" }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={() => setIsOpenSigned(false)}
                                aria-label="close"
                            >
                                <GridCloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                Kundenansicht
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ m: 5 }}>
                        <SignedContract
                            key={`signed-${contractObject.id}`}
                            idContract={contractObject.id}
                            enableAuftraggeberSigned={contractObject.ContractType !== 30}
                            contractType={contractObject.ContractType}
                            handleClose={() => setIsOpenSigned(false)}
                            handleSave={() => {
                                refetchContractArray();
                                setIsOpenSigned(false);
                            }}
                        />
                    </Box>
                </Dialog>
            )}

            {isPendingGenerateContractDocument && (
                <Dialog open>
                    <DialogContent>Dokumente werden generiert...</DialogContent>
                </Dialog>
            )}

            <TableRow key={`contract-${contractObject.id}`}>
                <TableCell>{contractObject.id}</TableCell>
                <TableCell>
                    {contractTypeArray.find((x) => x.id === contractObject.ContractType)?.contractType}
                </TableCell>
                <TableCell>
                    {contractStateArray.find((x) => x.id === contractObject.ContractState)?.contractState}
                </TableCell>
                <TableCell>{contractObject.title}</TableCell>
                <TableCell>
                    <IconButton onClick={handleOpenEditor}>
                        <Edit />
                    </IconButton>
                    {contractObject.ContractDocuments.length === 0 ? (
                        <>
                            <IconButton onClick={() => setIsOpenSigned(true)}>
                                <NoteAlt />
                            </IconButton>
                            <IconButton
                                onClick={() =>
                                    generatePdf(
                                        base64Download,
                                        () => {
                                            showFeedback("Die Dokumente konnten nicht generiert werden!!", "error");
                                        },
                                        contractObject,
                                    )
                                }
                            >
                                <Download />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <IconButton
                                onClick={() => base64Download(contractObject.ContractDocuments[0].Document_Signed)}
                            >
                                <Download />
                            </IconButton>
                        </>
                    )}
                </TableCell>
            </TableRow>
        </>
    );
};
